/** XeAgents.ai**/
import { useState, useMemo, Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { format, formatDistanceToNow, isValid } from "date-fns";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DataPagination, usePagination } from "@/components/ui/data-pagination";
import { formatDate } from "@/lib/date-format";
import { useToast } from "@/hooks/use-toast";
import {
  Search, Download, Loader2, Users, ChevronDown, Target, Mail, Phone, Eye,
  PhoneIncoming, PhoneOutgoing, Clock, Sparkles, FileText, Mic, CheckCircle2,
  XCircle, CalendarClock, Gauge, Database, ClipboardCheck, Radio, Layers,
} from "lucide-react";

interface Call {
  id: string;
  contactId: string | null;
  campaignId: string | null;
  phoneNumber?: string | null;
  status: string;
  duration: number | null;
  classification: string | null;
  sentiment: string | null;
  recordingUrl: string | null;
  transcript: string | null;
  aiSummary: string | null;
  metadata: Record<string, any> | null;
  startedAt: string | null;
  endedAt: string | null;
  createdAt: string;
  callDirection: string | null;
  elevenLabsConversationId?: string | null;
  conversationId?: string | null;
  agentId?: string | null;
  voiceProvider?: string | null;
  telephonyProvider?: string | null;
  creditsDeducted?: number | null;
  actualCostUsd?: number | null;
  callOutcome?: string | null;
  qualificationScore?: number | null;
  qualificationDecision?: string | null;
  dealBreakersResult?: Record<string, any> | null;
  scoringBreakdown?: Record<string, any> | null;
  wasTransferred?: boolean | null;
  transferredTo?: string | null;
  transferredAt?: string | null;
  campaign?: { id: string; name: string; status?: string | null; createdAt?: string | null } | null;
  contact?: {
    id: string;
    firstName: string;
    lastName?: string | null;
    phone: string;
    email?: string | null;
    customFields?: Record<string, any> | null;
    status?: string | null;
    callbackScheduledFor?: string | null;
    callbackTimezone?: string | null;
    callbackReason?: string | null;
    callbackStatus?: string | null;
    callbackAttempts?: number | null;
  } | null;
  incomingConnection?: { id: string; agentId: string } | null;
}

// One row per caller per campaign: every call that caller had, plus everything
// the platform captured about them.
interface Lead {
  key: string;
  campaignKey: string;
  campaignName: string;
  contactId: string | null;
  name: string;
  phone: string;
  email: string | null;
  customFields: Record<string, any> | null;
  contactStatus: string | null;
  callbackScheduledFor: string | null;
  callbackReason: string | null;
  callbackStatus: string | null;
  calls: Call[];
  latest: Call;
  totalCalls: number;
  answeredCalls: number;
  totalDuration: number;
  classification: string | null;
  sentiment: string | null;
  outcome: string | null;
  qualificationScore: number | null;
  qualificationDecision: string | null;
  lastCallAt: number;
  hasRecording: boolean;
  hasTranscript: boolean;
}

const NO_CAMPAIGN = "none";

// How many leads a campaign section shows before "show all"
const GROUP_PREVIEW_SIZE = 10;

// How many campaigns fit on one page before paging
const CAMPAIGNS_PER_PAGE = 10;

// Junk keys that bad CSV imports leave behind in custom_fields
const IGNORED_CUSTOM_FIELD_KEYS = ["__parsed_extra"];

const callTimestamp = (call: Call) => {
  const value = call.startedAt || call.createdAt;
  const time = value ? new Date(value).getTime() : 0;
  return isNaN(time) ? 0 : time;
};

const formatDuration = (seconds: number | null) => {
  if (!seconds) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const relativeTime = (value: string | number | null) => {
  if (!value) return "—";
  const date = new Date(value);
  if (!isValid(date)) return "—";
  return formatDistanceToNow(date, { addSuffix: true });
};

const absoluteTime = (value: string | number | null) => {
  if (!value) return "—";
  const date = new Date(value);
  if (!isValid(date)) return "—";
  return format(date, "MMM d, yyyy h:mm a");
};

// Whatever the agent wrote back after the call
const analysisOf = (call: Call): Record<string, any> | null => {
  const analysis = call.metadata?.elevenLabsAnalysis;
  return analysis && typeof analysis === "object" ? analysis : null;
};

const stringifyValue = (value: any): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.map(stringifyValue).filter(Boolean).join(", ");
  if (typeof value === "object") {
    // Data collection results arrive as { value, rationale, json_schema }
    if ("value" in value) return stringifyValue((value as any).value);
    return JSON.stringify(value);
  }
  return String(value);
};

// The keys a campaign actually captured, minus the import junk
const usableEntries = (data: Record<string, any> | null | undefined) => {
  if (!data || typeof data !== "object") return [] as Array<[string, any]>;
  return Object.entries(data).filter(([key, value]) => {
    if (IGNORED_CUSTOM_FIELD_KEYS.includes(key)) return false;
    if (key.startsWith("%PDF")) return false;
    return value !== null && value !== undefined && stringifyValue(value) !== "";
  });
};

const LEAD_LABELS: Record<string, { label: string; className: string }> = {
  hot: { label: "Hot Lead", className: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20" },
  warm: { label: "Warm Lead", className: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20" },
  cold: { label: "Cold Lead", className: "bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-500/20" },
  lost: { label: "Not Interested", className: "bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/20" },
  completed_successful: { label: "Successful", className: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20" },
  completed_failed: { label: "Failed", className: "bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/20" },
  completed: { label: "Completed", className: "bg-slate-500/10 text-slate-700 dark:text-slate-400 border-slate-500/20" },
};

const SENTIMENT_LABELS: Record<string, string> = {
  positive: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20",
  neutral: "bg-slate-500/10 text-slate-700 dark:text-slate-400 border-slate-500/20",
  negative: "bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/20",
};

export default function Leads() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [campaignFilter, setCampaignFilter] = useState("all");
  const [leadFilter, setLeadFilter] = useState("all");
  const [sentimentFilter, setSentimentFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({});
  const [groupByCampaign, setGroupByCampaign] = useState(true);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [campaignPage, setCampaignPage] = useState(1);
  const [campaignsPerPage, setCampaignsPerPage] = useState(CAMPAIGNS_PER_PAGE);

  const { data: calls, isLoading } = useQuery<Call[]>({
    queryKey: ["/api/calls"],
  });

  // Roll every call up to the caller who had it
  const leads = useMemo<Lead[]>(() => {
    const byCaller = new Map<string, Lead>();

    for (const call of calls || []) {
      const campaignKey = call.campaign?.id || call.campaignId || NO_CAMPAIGN;
      const campaignName = call.campaign?.name || (campaignKey === NO_CAMPAIGN ? "No Campaign" : `Campaign ${campaignKey.slice(0, 8)}`);
      const callerKey = call.contact?.id || call.contactId || call.contact?.phone || call.phoneNumber || call.id;
      const key = `${campaignKey}::${callerKey}`;

      const contactName = call.contact && call.contact.firstName && call.contact.firstName.toLowerCase() !== "unknown"
        ? `${call.contact.firstName} ${call.contact.lastName || ""}`.trim()
        : "";
      const phone = call.contact?.phone || call.phoneNumber || "";

      const existing = byCaller.get(key);
      if (existing) {
        existing.calls.push(call);
      } else {
        byCaller.set(key, {
          key,
          campaignKey,
          campaignName,
          contactId: call.contact?.id || call.contactId || null,
          name: contactName || phone || `Caller ${callerKey.slice(0, 8)}`,
          phone,
          email: call.contact?.email || null,
          customFields: call.contact?.customFields || null,
          contactStatus: call.contact?.status || null,
          callbackScheduledFor: call.contact?.callbackScheduledFor || null,
          callbackReason: call.contact?.callbackReason || null,
          callbackStatus: call.contact?.callbackStatus || null,
          calls: [call],
          latest: call,
          totalCalls: 0,
          answeredCalls: 0,
          totalDuration: 0,
          classification: null,
          sentiment: null,
          outcome: null,
          qualificationScore: null,
          qualificationDecision: null,
          lastCallAt: 0,
          hasRecording: false,
          hasTranscript: false,
        });
      }
    }

    // Newest call first, and the newest non-empty value wins for each summary field
    for (const lead of Array.from(byCaller.values())) {
      lead.calls.sort((a, b) => callTimestamp(b) - callTimestamp(a));
      lead.latest = lead.calls[0];
      lead.lastCallAt = callTimestamp(lead.calls[0]);
      lead.totalCalls = lead.calls.length;
      lead.answeredCalls = lead.calls.filter(call => call.status === "completed").length;
      lead.totalDuration = lead.calls.reduce((total, call) => total + (call.duration || 0), 0);
      lead.hasRecording = lead.calls.some(call => call.recordingUrl || call.elevenLabsConversationId);
      lead.hasTranscript = lead.calls.some(call => Boolean(call.transcript));

      const firstWith = <T,>(pick: (call: Call) => T | null | undefined): T | null => {
        for (const call of lead.calls) {
          const value = pick(call);
          if (value !== null && value !== undefined && value !== "") return value;
        }
        return null;
      };

      lead.classification = firstWith(call => call.classification);
      lead.sentiment = firstWith(call => call.sentiment);
      lead.outcome = firstWith(call => call.callOutcome);
      lead.qualificationScore = firstWith(call => call.qualificationScore);
      lead.qualificationDecision = firstWith(call => call.qualificationDecision);
      if (!lead.email) lead.email = firstWith(call => call.contact?.email || null);
      if (!lead.customFields) lead.customFields = firstWith(call => call.contact?.customFields || null);
    }

    return Array.from(byCaller.values());
  }, [calls]);

  // Campaigns that actually have leads, most recently active first
  const campaignOptions = useMemo(() => {
    const seen = new Map<string, { key: string; name: string; count: number; lastRunAt: number }>();
    for (const lead of leads) {
      const existing = seen.get(lead.campaignKey);
      if (existing) {
        existing.count += 1;
        if (lead.lastCallAt > existing.lastRunAt) existing.lastRunAt = lead.lastCallAt;
      } else {
        seen.set(lead.campaignKey, { key: lead.campaignKey, name: lead.campaignName, count: 1, lastRunAt: lead.lastCallAt });
      }
    }
    return Array.from(seen.values()).sort((a, b) => b.lastRunAt - a.lastRunAt);
  }, [leads]);

  const filteredLeads = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    const matches = leads.filter((lead) => {
      const haystack = [
        lead.name,
        lead.phone,
        lead.email || "",
        lead.campaignName,
        lead.latest.aiSummary || "",
        analysisOf(lead.latest)?.transcript_summary || "",
        usableEntries(lead.customFields).map(([key, value]) => `${key} ${stringifyValue(value)}`).join(" "),
      ].join(" ").toLowerCase();

      const matchesSearch = query === "" || haystack.includes(query);
      const matchesCampaign = campaignFilter === "all" || lead.campaignKey === campaignFilter;
      const matchesLead = leadFilter === "all" || lead.classification === leadFilter;
      const matchesSentiment = sentimentFilter === "all" || lead.sentiment === sentimentFilter;

      return matchesSearch && matchesCampaign && matchesLead && matchesSentiment;
    });

    const leadRank: Record<string, number> = { hot: 4, warm: 3, cold: 2, lost: 1 };

    return matches.sort((a, b) => {
      switch (sortBy) {
        case "calls":
          return b.totalCalls - a.totalCalls;
        case "talkTime":
          return b.totalDuration - a.totalDuration;
        case "quality":
          return (leadRank[b.classification || ""] || 0) - (leadRank[a.classification || ""] || 0);
        case "name":
          return a.name.localeCompare(b.name);
        default:
          return b.lastCallAt - a.lastCallAt;
      }
    });
  }, [leads, searchQuery, campaignFilter, leadFilter, sentimentFilter, sortBy]);

  const pagination = usePagination(filteredLeads, 25);

  const stats = useMemo(() => ({
    total: leads.length,
    hot: leads.filter(lead => lead.classification === "hot").length,
    warm: leads.filter(lead => lead.classification === "warm").length,
    cold: leads.filter(lead => lead.classification === "cold").length,
    notInterested: leads.filter(lead => lead.classification === "lost").length,
    withEmail: leads.filter(lead => Boolean(lead.email)).length,
  }), [leads]);

  // Source-of-truth export: fixed columns plus every captured field we have seen
  const handleExport = () => {
    if (filteredLeads.length === 0) {
      toast({ title: "Nothing to export", description: "No leads match the current filters." });
      return;
    }

    const customKeys: string[] = [];
    const collectedKeys: string[] = [];
    for (const lead of filteredLeads) {
      for (const [key] of usableEntries(lead.customFields)) {
        if (!customKeys.includes(key)) customKeys.push(key);
      }
      for (const [key] of usableEntries(analysisOf(lead.latest)?.data_collection_results)) {
        if (!collectedKeys.includes(key)) collectedKeys.push(key);
      }
    }

    const headers = [
      "Campaign", "Contact ID", "Name", "Phone", "Email", "Contact Status",
      "Lead Quality", "Sentiment", "Call Outcome", "Qualification Score", "Qualification Decision",
      "Total Calls", "Answered Calls", "Total Talk Time (s)", "Last Call At", "Last Call Status",
      "Direction", "Agent ID", "Voice Provider", "Telephony Provider", "Credits Deducted",
      "Transferred To", "Callback Scheduled For", "Callback Reason", "Callback Status",
      "Has Recording", "Has Transcript", "Call Successful", "Call Success Score", "Call Summary Title",
      "AI Summary",
      ...customKeys.map(key => `Field: ${key}`),
      ...collectedKeys.map(key => `Collected: ${key}`),
    ];

    const rows = filteredLeads.map((lead) => {
      const analysis = analysisOf(lead.latest);
      const collected = analysis?.data_collection_results || {};
      const fields = lead.customFields || {};
      return [
        lead.campaignName,
        lead.contactId || "",
        lead.name,
        lead.phone,
        lead.email || "",
        lead.contactStatus || "",
        LEAD_LABELS[lead.classification || ""]?.label || lead.classification || "",
        lead.sentiment || "",
        lead.outcome || "",
        lead.qualificationScore ?? "",
        lead.qualificationDecision || "",
        lead.totalCalls,
        lead.answeredCalls,
        lead.totalDuration,
        lead.lastCallAt ? formatDate(new Date(lead.lastCallAt).toISOString(), "yyyy-MM-dd HH:mm:ss") : "",
        lead.latest.status,
        lead.latest.callDirection || "",
        lead.latest.agentId || "",
        lead.latest.voiceProvider || "",
        lead.latest.telephonyProvider || "",
        lead.latest.creditsDeducted ?? "",
        lead.latest.transferredTo || "",
        lead.callbackScheduledFor ? formatDate(lead.callbackScheduledFor, "yyyy-MM-dd HH:mm:ss") : "",
        lead.callbackReason || "",
        lead.callbackStatus || "",
        lead.hasRecording ? "yes" : "no",
        lead.hasTranscript ? "yes" : "no",
        analysis?.call_successful || "",
        analysis?.call_success_score ?? "",
        analysis?.call_summary_title || "",
        lead.latest.aiSummary || analysis?.transcript_summary || "",
        ...customKeys.map(key => stringifyValue((fields as any)[key])),
        ...collectedKeys.map(key => stringifyValue((collected as any)[key])),
      ];
    });

    const csv = [headers, ...rows]
      .map(row => row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `leads-export-${format(new Date(), "yyyy-MM-dd")}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const leadBadge = (classification: string | null) => {
    if (!classification) return <span className="text-muted-foreground text-xs">Unclassified</span>;
    const meta = LEAD_LABELS[classification];
    if (!meta) return <Badge variant="outline">{classification}</Badge>;
    return <Badge className={meta.className}>{meta.label}</Badge>;
  };

  const sentimentBadge = (sentiment: string | null) => {
    if (!sentiment) return <span className="text-muted-foreground text-xs">—</span>;
    const className = SENTIMENT_LABELS[sentiment];
    if (!className) return <Badge variant="outline">{sentiment}</Badge>;
    return <Badge className={className}>{sentiment.charAt(0).toUpperCase() + sentiment.slice(1)}</Badge>;
  };

  // Drops the rows we have nothing for, so a section never shows empty fields
  const presentEntries = (pairs: Array<[string, any]>) =>
    pairs.filter(([, value]) => value !== null && value !== undefined && value !== "");

  const detailSection = (title: string, icon: React.ReactNode, entries: Array<[string, any]>) => {
    if (entries.length === 0) return null;
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {icon}
          {title}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5">
          {entries.map(([key, value]) => (
            <div key={key} className="flex items-start gap-2 text-sm min-w-0">
              <span className="text-muted-foreground flex-shrink-0">{key}:</span>
              <span className="text-foreground break-words min-w-0">{stringifyValue(value) || "—"}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // The table itself, so the grouped and flat views stay identical
  const leadsTable = (rows: Lead[]) => (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]"></TableHead>
                    <TableHead>Caller</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Lead</TableHead>
                    <TableHead>Sentiment</TableHead>
                    <TableHead>Outcome</TableHead>
                    <TableHead className="text-right">Score</TableHead>
                    <TableHead className="text-right">Calls</TableHead>
                    <TableHead className="text-right">Talk time</TableHead>
                    <TableHead>Last call</TableHead>
                    <TableHead className="text-right">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((lead) => {
                    const isOpen = Boolean(openRows[lead.key]);
                    const analysis = analysisOf(lead.latest);
                    const collected = usableEntries(analysis?.data_collection_results);
                    const evaluations = usableEntries(analysis?.evaluation_criteria_results);
                    const fields = usableEntries(lead.customFields);

                    return (
                      <Fragment key={lead.key}>
                        <TableRow
                          className="cursor-pointer"
                          onClick={() => setOpenRows(prev => ({ ...prev, [lead.key]: !prev[lead.key] }))}
                          data-testid={`row-lead-${lead.key}`}
                        >
                          <TableCell>
                            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${isOpen ? "" : "-rotate-90"}`} />
                          </TableCell>
                          <TableCell>
                            <div className="font-medium text-foreground">{lead.name}</div>
                            <div className="text-xs text-muted-foreground font-mono">{lead.phone || "—"}</div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {lead.email
                              ? <span className="text-foreground break-all">{lead.email}</span>
                              : <span className="text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5 text-sm">
                              <Target className="h-3 w-3 text-orange-500 flex-shrink-0" />
                              <span className="truncate max-w-[160px]">{lead.campaignName}</span>
                            </div>
                          </TableCell>
                          <TableCell>{leadBadge(lead.classification)}</TableCell>
                          <TableCell>{sentimentBadge(lead.sentiment)}</TableCell>
                          <TableCell className="text-sm">
                            {lead.outcome
                              ? <span className="capitalize">{lead.outcome.replace(/_/g, " ")}</span>
                              : <span className="text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            {lead.qualificationScore !== null
                              ? <span className="font-medium">{lead.qualificationScore}</span>
                              : <span className="text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell className="text-right text-sm font-medium">{lead.totalCalls}</TableCell>
                          <TableCell className="text-right text-sm">{formatDuration(lead.totalDuration)}</TableCell>
                          <TableCell className="text-sm whitespace-nowrap">{relativeTime(lead.lastCallAt)}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setLocation(`/app/calls/${lead.latest.id}`);
                              }}
                              data-testid={`button-open-call-${lead.key}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>

                        {isOpen && (
                          <TableRow className="bg-muted/30 hover:bg-muted/30">
                            <TableCell colSpan={12} className="p-5">
                              <div className="space-y-5">
                                {detailSection("Contact", <Phone className="h-3.5 w-3.5" />, presentEntries([
                                  ["Name", lead.name],
                                  ["Phone", lead.phone],
                                  ["Email", lead.email],
                                  ["Contact ID", lead.contactId],
                                  ["Contact status", lead.contactStatus],
                                  ["Campaign", lead.campaignName],
                                  ["Callback scheduled", lead.callbackScheduledFor ? absoluteTime(lead.callbackScheduledFor) : null],
                                  ["Callback reason", lead.callbackReason],
                                  ["Callback status", lead.callbackStatus],
                                ]))}

                                {detailSection("Captured fields", <Database className="h-3.5 w-3.5" />, fields)}

                                {detailSection("Data collected on the call", <ClipboardCheck className="h-3.5 w-3.5" />, collected)}

                                {detailSection("Evaluation criteria", <Gauge className="h-3.5 w-3.5" />, evaluations)}

                                {detailSection("Qualification", <Gauge className="h-3.5 w-3.5" />, presentEntries([
                                  ["Score", lead.qualificationScore],
                                  ["Decision", lead.qualificationDecision],
                                  ["Deal breakers", lead.latest.dealBreakersResult],
                                  ["Scoring breakdown", lead.latest.scoringBreakdown],
                                  ["Call successful", analysis?.call_successful],
                                  ["Call success score", analysis?.call_success_score],
                                  ["Sentiment analysis", analysis?.sentiment_analysis],
                                ]))}

                                {detailSection("Telephony", <Radio className="h-3.5 w-3.5" />, presentEntries([
                                  ["Direction", lead.latest.callDirection],
                                  ["Agent ID", lead.latest.agentId],
                                  ["Voice provider", lead.latest.voiceProvider],
                                  ["Telephony provider", lead.latest.telephonyProvider],
                                  ["Credits deducted", lead.latest.creditsDeducted],
                                  ["Conversation ID", lead.latest.elevenLabsConversationId || lead.latest.conversationId],
                                  ["Transferred to", lead.latest.transferredTo],
                                  ["Transferred at", lead.latest.transferredAt ? absoluteTime(lead.latest.transferredAt) : null],
                                ]))}

                                {(lead.latest.aiSummary || analysis?.transcript_summary) && (
                                  <div className="space-y-2">
                                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                      <Sparkles className="h-3.5 w-3.5" />
                                      {analysis?.call_summary_title || "AI summary"}
                                    </div>
                                    <p className="text-sm text-foreground">
                                      {lead.latest.aiSummary || analysis?.transcript_summary}
                                    </p>
                                  </div>
                                )}

                                <div className="space-y-2">
                                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                    <Clock className="h-3.5 w-3.5" />
                                    Call history ({lead.calls.length})
                                  </div>
                                  <div className="space-y-2">
                                    {lead.calls.map((call) => (
                                      <div
                                        key={call.id}
                                        className="flex items-center justify-between gap-3 bg-background border rounded-md px-3 py-2"
                                        data-testid={`row-lead-call-${call.id}`}
                                      >
                                        <div className="flex items-center gap-3 min-w-0">
                                          {call.callDirection === "incoming"
                                            ? <PhoneIncoming className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                                            : <PhoneOutgoing className="h-4 w-4 text-blue-500 flex-shrink-0" />}
                                          <div className="min-w-0">
                                            <div className="text-sm text-foreground">
                                              {absoluteTime(call.startedAt || call.createdAt)}
                                              <span className="text-muted-foreground"> · {formatDuration(call.duration)} · {call.status}</span>
                                            </div>
                                            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                                              {(call.recordingUrl || call.elevenLabsConversationId) && (
                                                <span className="flex items-center gap-1"><Mic className="h-3 w-3" />Recording</span>
                                              )}
                                              {call.transcript && (
                                                <span className="flex items-center gap-1"><FileText className="h-3 w-3" />Transcript</span>
                                              )}
                                              {call.classification && (
                                                <span>{LEAD_LABELS[call.classification]?.label || call.classification}</span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => setLocation(`/app/calls/${call.id}`)}
                                          data-testid={`button-view-call-${call.id}`}
                                        >
                                          <Eye className="h-4 w-4 mr-1" />
                                          View
                                        </Button>
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {lead.callbackScheduledFor && (
                                  <div className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-400">
                                    <CalendarClock className="h-4 w-4" />
                                    Callback {relativeTime(lead.callbackScheduledFor)} — {lead.callbackReason || "no reason recorded"}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </Card>
  );

  // Leads bucketed by the campaign that produced them, most recently active first
  const groupLeadsByCampaign = (list: Lead[]) => {
    const groups = new Map<string, { key: string; name: string; leads: Lead[]; lastRunAt: number }>();
    for (const lead of list) {
      const existing = groups.get(lead.campaignKey);
      if (existing) {
        existing.leads.push(lead);
        if (lead.lastCallAt > existing.lastRunAt) existing.lastRunAt = lead.lastCallAt;
      } else {
        groups.set(lead.campaignKey, { key: lead.campaignKey, name: lead.campaignName, leads: [lead], lastRunAt: lead.lastCallAt });
      }
    }
    return Array.from(groups.values()).sort((a, b) => b.lastRunAt - a.lastRunAt);
  };

  // Campaign sections, closed until asked for, ten campaigns to a page
  const renderCampaignGroups = () => {
    const groups = groupLeadsByCampaign(filteredLeads);
    const totalCampaignPages = Math.max(1, Math.ceil(groups.length / campaignsPerPage));
    const currentCampaignPage = Math.min(campaignPage, totalCampaignPages);
    const pageStart = (currentCampaignPage - 1) * campaignsPerPage;
    const visibleGroups = groups.slice(pageStart, pageStart + campaignsPerPage);

    return (
      <div className="space-y-4">
        {visibleGroups.map((group) => {
          const isOpen = openGroups[group.key] ?? campaignFilter !== "all";
          const isExpanded = expandedGroups[group.key];
          const visibleLeads = isExpanded ? group.leads : group.leads.slice(0, GROUP_PREVIEW_SIZE);
          const hot = group.leads.filter(lead => lead.classification === "hot").length;
          const warm = group.leads.filter(lead => lead.classification === "warm").length;
          const withEmail = group.leads.filter(lead => Boolean(lead.email)).length;

          return (
            <div
              key={group.key}
              className="border rounded-lg bg-muted/30 overflow-hidden"
              data-testid={`group-leads-${group.key}`}
            >
              <button
                type="button"
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/60 transition-colors"
                onClick={() => setOpenGroups(prev => ({ ...prev, [group.key]: !(prev[group.key] ?? campaignFilter !== "all") }))}
                data-testid={`button-toggle-leads-group-${group.key}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    group.key === NO_CAMPAIGN
                      ? 'bg-slate-500/10 text-slate-600 dark:text-slate-400'
                      : 'bg-orange-500/10 text-orange-600 dark:text-orange-400'
                  }`}>
                    <Target className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-foreground truncate">{group.name}</h3>
                      <Badge variant="secondary" className="flex-shrink-0">{group.leads.length}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      {group.lastRunAt > 0 && <>Last call {relativeTime(group.lastRunAt)}{' · '}</>}
                      {hot} hot · {warm} warm · {withEmail} with email
                    </p>
                  </div>
                </div>
                <ChevronDown className={`h-4 w-4 text-muted-foreground flex-shrink-0 transition-transform duration-200 ${isOpen ? '' : '-rotate-90'}`} />
              </button>

              {isOpen && (
                <div className="px-3 pb-3 space-y-3">
                  {leadsTable(visibleLeads)}
                  {group.leads.length > GROUP_PREVIEW_SIZE && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full"
                      onClick={() => setExpandedGroups(prev => ({ ...prev, [group.key]: !prev[group.key] }))}
                      data-testid={`button-expand-leads-group-${group.key}`}
                    >
                      {isExpanded ? "Show less" : `Show all ${group.leads.length} leads`}
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        <DataPagination
          currentPage={currentCampaignPage}
          totalPages={totalCampaignPages}
          totalItems={groups.length}
          itemsPerPage={campaignsPerPage}
          onPageChange={setCampaignPage}
          onItemsPerPageChange={(size) => {
            setCampaignsPerPage(size);
            setCampaignPage(1);
          }}
          data-testid="pagination-lead-campaigns"
        />
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 dark:from-violet-950/40 dark:via-purple-950/30 dark:to-fuchsia-950/40 border p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg">
              <Users className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Leads</h1>
              <p className="text-muted-foreground text-sm">
                Every caller, campaign by campaign, with the full record of what was captured
              </p>
            </div>
          </div>
          <Button variant="default" onClick={handleExport} data-testid="button-export-leads">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-6">
          <div className="bg-background/60 backdrop-blur-sm rounded-lg p-3 border">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-violet-600 dark:text-violet-400" />
              <span className="text-2xl font-bold text-foreground" data-testid="text-total-leads">{stats.total}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Total Leads</p>
          </div>
          <div className="bg-background/60 backdrop-blur-sm rounded-lg p-3 border">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              <span className="text-2xl font-bold text-foreground">{stats.hot}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Hot</p>
          </div>
          <div className="bg-background/60 backdrop-blur-sm rounded-lg p-3 border">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <span className="text-2xl font-bold text-foreground">{stats.warm}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Warm</p>
          </div>
          <div className="bg-background/60 backdrop-blur-sm rounded-lg p-3 border">
            <div className="flex items-center gap-2">
              <Radio className="h-4 w-4 text-sky-600 dark:text-sky-400" />
              <span className="text-2xl font-bold text-foreground">{stats.cold}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Cold</p>
          </div>
          <div className="bg-background/60 backdrop-blur-sm rounded-lg p-3 border">
            <div className="flex items-center gap-2">
              <XCircle className="h-4 w-4 text-rose-600 dark:text-rose-400" />
              <span className="text-2xl font-bold text-foreground">{stats.notInterested}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Not Interested</p>
          </div>
          <div className="bg-background/60 backdrop-blur-sm rounded-lg p-3 border">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-2xl font-bold text-foreground">{stats.withEmail}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">With Email</p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] w-full sm:w-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search name, phone, email, company, summary..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="input-search-leads"
          />
        </div>
        <Select value={campaignFilter} onValueChange={setCampaignFilter}>
          <SelectTrigger className="w-full sm:w-[220px]" data-testid="select-leads-campaign">
            <SelectValue placeholder="Campaign" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Campaigns</SelectItem>
            {campaignOptions.map((option) => (
              <SelectItem key={option.key} value={option.key}>
                <span className="flex items-center gap-2">
                  <span className="truncate">{option.name} ({option.count})</span>
                  {option.lastRunAt > 0 && (
                    <span className="text-xs text-muted-foreground flex-shrink-0">{relativeTime(option.lastRunAt)}</span>
                  )}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={leadFilter} onValueChange={setLeadFilter}>
          <SelectTrigger className="w-full sm:w-[150px]" data-testid="select-leads-quality">
            <SelectValue placeholder="Lead Quality" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Leads</SelectItem>
            <SelectItem value="hot">Hot Lead</SelectItem>
            <SelectItem value="warm">Warm Lead</SelectItem>
            <SelectItem value="cold">Cold Lead</SelectItem>
            <SelectItem value="lost">Not Interested</SelectItem>
            <SelectItem value="completed_successful">Successful</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sentimentFilter} onValueChange={setSentimentFilter}>
          <SelectTrigger className="w-full sm:w-[150px]" data-testid="select-leads-sentiment">
            <SelectValue placeholder="Sentiment" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sentiment</SelectItem>
            <SelectItem value="positive">Positive</SelectItem>
            <SelectItem value="neutral">Neutral</SelectItem>
            <SelectItem value="negative">Negative</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-full sm:w-[170px]" data-testid="select-leads-sort">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Latest call first</SelectItem>
            <SelectItem value="calls">Most calls</SelectItem>
            <SelectItem value="talkTime">Longest talk time</SelectItem>
            <SelectItem value="quality">Best lead quality</SelectItem>
            <SelectItem value="name">Name A–Z</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant={groupByCampaign ? "default" : "outline"}
          onClick={() => setGroupByCampaign(prev => !prev)}
          className="w-full sm:w-auto"
          data-testid="button-toggle-leads-grouping"
        >
          <Layers className="h-4 w-4 mr-2" />
          {groupByCampaign ? "By campaign" : "Flat list"}
        </Button>
      </div>

      {filteredLeads.length === 0 ? (
        <Card className="p-12 text-center">
          <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="font-medium text-lg mb-1">No leads found</h3>
          <p className="text-muted-foreground text-sm">
            {leads.length === 0
              ? "Leads appear here once a campaign has made calls."
              : "No leads match your filters."}
          </p>
        </Card>
      ) : groupByCampaign ? (
        renderCampaignGroups()
      ) : (
        <div className="space-y-4">
          {leadsTable(pagination.paginatedItems)}

          <DataPagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            itemsPerPage={pagination.itemsPerPage}
            onPageChange={pagination.handlePageChange}
            onItemsPerPageChange={pagination.handleItemsPerPageChange}
            data-testid="pagination-leads"
          />
        </div>
      )}
    </div>
  );
}
